// packages/frontend/src/hooks/useAdmin.ts
import { useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { firestore } from '../services/firebase';
import { useAuth } from './useAuth';
import { logger } from '../utils/logger';

/**
 * Hook to check the current user's admin role.
 * Uses Firebase Auth UID to look up user document in Firestore.
 *
 * A failed role read is surfaced via `error` and is NOT treated as "not
 * admin": the server independently enforces the same role checks, so the
 * only risk of exposing a real error here is a confusing UI on a network
 * blip, not an authorization bypass.
 */
export function useAdmin() {
  const { firebaseUser, isAuthenticated } = useAuth();
  const [role, setRole] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function checkAdminRole() {
      // Need Firebase Auth UID to look up user document
      if (!isAuthenticated || !firebaseUser?.uid) {
        if (!cancelled) {
          setRole(null);
          setError(null);
          setIsLoading(false);
        }
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        // Get user document from Firestore using Firebase Auth UID as document ID
        // The backend stores users at users/{firebaseAuthUID}
        const userDoc = await getDoc(doc(firestore, 'users', firebaseUser.uid));
        if (cancelled) return;

        if (userDoc.exists()) {
          const userData = userDoc.data();
          const fetchedRole = typeof userData.role === 'string' ? userData.role : null;
          setRole(fetchedRole);
          logger.debug('Admin role check', {
            firebaseUid: firebaseUser.uid,
            role: fetchedRole,
          });
        } else {
          logger.warn('User document not found in Firestore', {
            firebaseUid: firebaseUser.uid,
          });
          setRole(null);
        }
      } catch (err) {
        if (cancelled) return;
        logger.error('Failed to check admin role', err);
        setRole(null);
        setError(err instanceof Error ? err : new Error('Failed to check admin role'));
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    checkAdminRole();

    return () => {
      cancelled = true;
    };
  }, [firebaseUser?.uid, isAuthenticated]);

  const isAdmin = role === 'admin' || role === 'super_admin';
  const isSuperAdmin = role === 'super_admin';

  return { isAdmin, isSuperAdmin, isLoading, error };
}
